import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  BanknotesIcon,
  CalendarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  UsersIcon,
  ArrowTrendingUpIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import { reportService } from '../../services/reportService'

interface Receivable {
  id: number
  type: 'invoice' | 'sale'
  number: string
  totalAmount: number
  balanceDue: number
  dueDate: string
  isOverdue: boolean
  daysOverdue: number
}

interface CustomerDebtor {
  id: number
  name: string
  email: string | null
  phone: string | null
  totalBalance: number
  overdueAmount: number
  dueByEndOfMonth: number
  dueInXDays: number
  creditLimit: number
  exceedsLimit: boolean
  receivableCount: number
  receivables: Receivable[]
}

interface GroupReport {
  id: number
  name: string
  code: string
  paymentTermsDays: number
  creditLimit: number
  customerCount: number
  totalBalance: number
  overdueAmount: number
  dueByEndOfMonth: number
  dueInXDays: number
  customers: CustomerDebtor[]
}

interface DebtorsSummary {
  totalOutstanding: number
  totalOverdue: number
  dueByEndOfMonth: number
  dueInXDays: number
  totalCustomers: number
}

export const DebtorsReportPage = () => {
  const navigate = useNavigate()
  const [daysThreshold, setDaysThreshold] = useState(30)
  const [expandedGroup, setExpandedGroup] = useState<number | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['debtors-report', daysThreshold],
    queryFn: () => reportService.getDebtorsReport({ days: daysThreshold }),
  })

  const report = data?.data?.data
  const summary: DebtorsSummary = report?.summary || {
    totalOutstanding: 0,
    totalOverdue: 0,
    dueByEndOfMonth: 0,
    dueInXDays: 0,
    totalCustomers: 0,
  }
  const groups: GroupReport[] = report?.groups || []

  const toggleGroup = (groupId: number) => {
    setExpandedGroup((prev) => (prev === groupId ? null : groupId))
  }

  const formatCurrency = (amount: number) => {
    return `KSh ${Number(amount).toLocaleString()}`
  }

  const widgets = [
    {
      title: 'Total Outstanding',
      value: formatCurrency(summary.totalOutstanding),
      icon: BanknotesIcon,
      color: 'bg-blue-100 text-blue-600',
    },
    {
      title: 'Total Overdue',
      value: formatCurrency(summary.totalOverdue),
      icon: ExclamationTriangleIcon,
      color: 'bg-red-100 text-red-600',
    },
    {
      title: 'Due by End of Month',
      value: formatCurrency(summary.dueByEndOfMonth),
      icon: CalendarIcon,
      color: 'bg-yellow-100 text-yellow-600',
    },
    {
      title: `Due in ${daysThreshold} Days`,
      value: formatCurrency(summary.dueInXDays),
      icon: ClockIcon,
      color: 'bg-purple-100 text-purple-600',
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Debtors Report</h1>
          <p className="text-sm text-gray-500 mt-1">
            Track outstanding balances by customer group
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Due in (days):</label>
          <select
            value={daysThreshold}
            onChange={(e) => setDaysThreshold(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
          >
            <option value={7}>7 days</option>
            <option value={15}>15 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
        </div>
      </div>

      {/* Widgets */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {widgets.map((widget) => (
          <div key={widget.title} className="bg-white rounded-xl shadow border border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${widget.color}`}>
                <widget.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{widget.title}</p>
                <p className="text-lg font-semibold text-gray-900">{widget.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Groups Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-xl shadow border border-gray-100 p-12 text-center">
          <UsersIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No debtors found</h3>
          <p className="text-gray-500">All customer balances are currently settled.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-10"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Customers</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Balance</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Overdue</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Due EOM</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Due in {daysThreshold}d</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Terms</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {groups.map((group) => (
                  <>
                    <tr
                      key={group.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleGroup(group.id)}
                    >
                      <td className="px-4 py-3">
                        {expandedGroup === group.id ? (
                          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{group.name}</p>
                        <p className="text-xs text-gray-500">{group.code}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {group.customerCount}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="text-sm font-semibold text-gray-900">
                          {formatCurrency(group.totalBalance)}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className={`text-sm font-medium ${group.overdueAmount > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {formatCurrency(group.overdueAmount)}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="text-sm text-gray-900">
                          {formatCurrency(group.dueByEndOfMonth)}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="text-sm text-gray-900">
                          {formatCurrency(group.dueInXDays)}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-xs text-gray-600">
                          {group.paymentTermsDays} days
                        </span>
                      </td>
                    </tr>

                    {/* Expanded Customer Details */}
                    {expandedGroup === group.id && group.customers.length > 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-4 bg-gray-50">
                          <div className="rounded-lg border border-gray-200 overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    Customer
                                  </th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                    Balance
                                  </th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                    Overdue
                                  </th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                    Due EOM
                                  </th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                    Credit Limit
                                  </th>
                                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                                    Items
                                  </th>
                                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                                    Status
                                  </th>
                                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 bg-white">
                                {group.customers.map((customer) => (
                                  <tr key={customer.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3">
                                      <p className="text-sm font-medium text-gray-900">
                                        {customer.name}
                                      </p>
                                      {customer.phone && (
                                        <p className="text-xs text-gray-500">{customer.phone}</p>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <p className="text-sm font-semibold text-gray-900">
                                        {formatCurrency(customer.totalBalance)}
                                      </p>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <p className={`text-sm font-medium ${customer.overdueAmount > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                        {formatCurrency(customer.overdueAmount)}
                                      </p>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <p className="text-sm text-gray-900">
                                        {formatCurrency(customer.dueByEndOfMonth)}
                                      </p>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <p className={`text-sm ${customer.exceedsLimit ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                                        {formatCurrency(customer.creditLimit)}
                                      </p>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                        {customer.receivableCount}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      {customer.exceedsLimit && (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                          <ExclamationTriangleIcon className="w-3 h-3" />
                                          Over Limit
                                        </span>
                                      )}
                                      {customer.overdueAmount > 0 && !customer.exceedsLimit && (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                          <ArrowTrendingUpIcon className="w-3 h-3" />
                                          Overdue
                                        </span>
                                      )}
                                      {!customer.exceedsLimit && customer.overdueAmount === 0 && customer.totalBalance > 0 && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                          Current
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          navigate('/reports/statements', { state: { customerId: customer.id } })
                                        }}
                                        className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-800 hover:underline"
                                      >
                                        <DocumentTextIcon className="w-3 h-3" />
                                        View Statement
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
